import { useEffect, useRef, useState } from 'react';

const navItems = [
  ['Work', '#approach'],
  ['Evidence', '#evidence'],
  ['Partners', '#partners'],
  ['Contact', '#contact'],
] as const;

type ChapterMedia =
  | {
      type: 'image';
      src: string;
      label?: string;
      alt?: string;
      position?: string;
    }
  | {
      type: 'video';
      src: string;
      alt?: string;
      label?: string;
      position?: string;
    };

type Chapter = {
  number: string;
  label: string;
  title: string;
  body: string;
  tags: string[];
  media: ChapterMedia;
};

const chapters: Chapter[] = [
  {
    number: '01',
    label: 'Brand and identity',
    title: 'Brand identity and packaging.',
    body: 'A complete identity and packaging system designed to give Marsi a distinctive presence across retail, hospitality, and campaign environments.',
    tags: ['Brand strategy', 'Visual identity', 'Design systems'],
    media: {
      type: 'image',
      src: '/work/work_D.jpg',
      label: 'Marsi brand identity and packaging project by Eastern Studio',
      position: 'center',
    },
  },
  {
    number: '02',
    label: 'Campaigns and experiences',
    title: 'A year designed to stay visible.',
    body: 'A flexible calendar system created for banks and colleges, balancing institutional identity with clear information, strong imagery, and practical day-to-day use.',
    tags: ['Creative direction', 'Campaigns', 'Launch programs'],
    media: {
      type: 'image',
      src: '/work/work_E.jpg',
      label: 'Annual calendar design project by Eastern Studio',
      position: 'center',
    },
  },
  {
    number: '03',
    label: 'Editorial design',
    title: 'Craft, presented with confidence.',
    body: 'A company brochure designed to bring the material, process, and character of the work forward while giving the business a clear and credible presentation for international buyers.',
    tags: ['Creative direction', 'Editorial design', 'Print production'],
    media: {
      type: 'image',
      src: '/work/work_F.jpg',
      alt: 'Handicraft company brochure designed by Eastern Studio',
      position: 'center',
    },
  },
  {
    number: '04',
    label: 'Motion and campaign',
    title: 'A beer story built for movement.',
    body: 'A short-form animation created for a beer brand, combining product, identity, and rhythm into a campaign asset designed for digital launches, social media, and event screens.',
    tags: ['Art direction', 'Motion design', 'Campaign animation'],
    media: {
      type: 'video',
      src: '/work/work_C.mp4',
      alt: 'Beer campaign animation created by Eastern Studio',
      position: 'center',
    },
  },
];

const partners = [
  { name: 'Classic Tech', src: '/partners/classic-tech.png' },
  { name: 'Saugat Printers Pvt. Ltd.', src: '/partners/saugat-printers.png' },
  { name: 'Thai Bull', src: '/partners/thai-bull.png' },
  { name: 'Care Nepal', src: '/partners/care-nepal.png' },
  {
    name: 'Oral & Dental Clinic Kathmandu',
    src: '/partners/oral-dental-clinic-kathmandu.png',
  },
  { name: 'Prime HD Times', src: '/partners/prime-hd-times.png' },
  {
    name: 'Tiger Brewery Industries Pvt. Ltd.',
    src: '/partners/tiger-brewery-industries.png',
  },
  {
    name: 'Rotary International',
    src: '/partners/rotary-international.png',
  },
  { name: 'Gateway BHM', src: '/partners/gateway-bhm.png' },
  { name: 'Stocks Nepal', src: '/partners/stocks-nepal.png' },
  {
    name: 'NIC Nepal International Collection',
    src: '/partners/nic-nepal-international-collection.png',
  },
  { name: 'Saipal Academy', src: '/partners/saipal-academy.png' },
  {
    name: 'NICE Television HD',
    src: '/partners/nice-television-hd.png',
  },
  { name: 'Mohar', src: '/partners/mohar.png' },
  {
    name: 'Royal Handicrafts, Inc.',
    src: '/partners/royal-handicrafts-inc.png',
  },
  {
    name: 'Neweins International Study Center',
    src: '/partners/neweins-international-study-center.png',
  },
];

function Arrow({ down = false }: { down?: boolean }) {
  return (
    <svg
      className={down ? 'arrow arrow--down' : 'arrow'}
      viewBox="0 0 48 18"
      aria-hidden="true"
    >
      <path d="M1 9h43" />
      <path d="m37 2 7 7-7 7" />
    </svg>
  );
}

function useScrollScene() {
  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;

    const update = () => {
      const maximum = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1,
      );

      root.style.setProperty('--scroll-y', `${window.scrollY}px`);
      root.style.setProperty(
        '--scroll-progress',
        `${window.scrollY / maximum}`,
      );

      frame = 0;
    };

    const onScroll = () => {
      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };

    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);

      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, []);
}

function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('menu-open', open);

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [open]);

  return (
    <header className="site-header">
      <a className="brand" href="#top" aria-label="Eastern Studio home">
        <img
          className="brand__logo"
          src="/brand/eastern_studio_logo.png"
          alt="Eastern Studio"
        />
      </a>

      <nav className="desktop-nav" aria-label="Primary navigation">
        {navItems.slice(0, 3).map(([label, href]) => (
          <a href={href} key={href}>
            {label}
          </a>
        ))}

        <a
          className="desktop-nav__contact"
          href="mailto:design@easternstudio.net"
        >
          Contact
        </a>
      </nav>

      <button
        className="menu-button"
        type="button"
        aria-expanded={open}
        aria-controls="mobile-menu"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((value) => !value)}
      >
        <span />
        <span />
      </button>

      <div
        className={`mobile-menu ${open ? 'mobile-menu--open' : ''}`}
        id="mobile-menu"
      >
        <p className="eyebrow">Navigation</p>

        {navItems.map(([label, href], index) => (
          <a
            href={href}
            key={href}
            onClick={() => setOpen(false)}
          >
            <small>0{index + 1}</small>
            <span>{label}</span>
          </a>
        ))}
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero__art" aria-hidden="true">
        <img src="/art/hero-field.svg" alt="" />
      </div>

      <div className="hero__copy">
        <h1>
          <span>From Nepal,</span>
          <span>with a point of view.</span>
        </h1>
      </div>

      <div className="scroll-cue" aria-hidden="true">
        <span>Scroll</span>
        <i />
      </div>
    </section>
  );
}

function Story() {
  const [active, setActive] = useState(0);

  const chapterRefs = useRef<Array<HTMLElement | null>>([]);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    const observers = chapterRefs.current.map((node, index) => {
      if (!node) {
        return null;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActive(index);
          }
        },
        {
          rootMargin: '-35% 0px -35% 0px',
          threshold: 0.01,
        },
      );

      observer.observe(node);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    videoRefs.current.forEach((video, index) => {
      if (!video) {
        return;
      }

      if (reducedMotion || index !== active) {
        video.pause();
        return;
      }

      void video.play().catch(() => {
        // The first loaded frame remains visible if autoplay is rejected.
      });
    });
  }, [active]);

  return (
    <section className="story" id="approach">
      <div className="section-title">
        <p className="eyebrow">Selected work / 01—03</p>

        <h2>
          Ideas made <em>visible.</em> Systems made usable.
        </h2>
      </div>

      <div className="story__layout">
        <div className="story__media">
          <div className="story__frame">
            {chapters.map((chapter, index) => {
              const isActive = index === active;
              const media = chapter.media;

              const mediaDescription =
                media.alt ??
                media.label ??
                `${chapter.title} — Eastern Studio project`;

              if (media.type === 'video') {
                return (
                  <video
                    className={`story__asset ${
                      isActive ? 'is-active' : ''
                    }`}
                    key={media.src}
                    ref={(node) => {
                      videoRefs.current[index] = node;
                    }}
                    src={media.src}
                    muted
                    loop
                    playsInline
                    preload={index === 0 ? 'auto' : 'metadata'}
                    aria-label={mediaDescription}
                    aria-hidden={!isActive}
                    tabIndex={-1}
                    style={{
                      objectPosition: media.position ?? 'center',
                    }}
                  />
                );
              }

              return (
                <img
                  className={`story__asset ${
                    isActive ? 'is-active' : ''
                  }`}
                  key={media.src}
                  src={media.src}
                  alt={isActive ? mediaDescription : ''}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  aria-hidden={!isActive}
                  style={{
                    objectPosition: media.position ?? 'center',
                  }}
                />
              );
            })}

            <div
              className="story__media-shade"
              aria-hidden="true"
            />

            <div className="story__caption">
              <span>{chapters[active].number}</span>
              <span>{chapters[active].label}</span>
            </div>
          </div>
        </div>

        <div className="story__chapters">
          {chapters.map((chapter, index) => {
            const mediaDescription =
              chapter.media.alt ??
              chapter.media.label ??
              `${chapter.title} — Eastern Studio project`;

            return (
              <article
                className={`chapter ${
                  index === active ? 'is-active' : ''
                }`}
                key={chapter.number}
                ref={(node) => {
                  chapterRefs.current[index] = node;
                }}
              >
                <div className="chapter__topline">
                  <span>{chapter.number}</span>
                  <span>{chapter.label}</span>
                </div>

                <div className="chapter__mobile-media">
                  {chapter.media.type === 'video' ? (
                    <video
                      src={chapter.media.src}
                      muted
                      loop
                      playsInline
                      autoPlay
                      preload="metadata"
                      aria-label={mediaDescription}
                      style={{
                        objectPosition:
                          chapter.media.position ?? 'center',
                      }}
                    />
                  ) : (
                    <img
                      src={chapter.media.src}
                      alt={mediaDescription}
                      loading="lazy"
                      decoding="async"
                      style={{
                        objectPosition:
                          chapter.media.position ?? 'center',
                      }}
                    />
                  )}
                </div>

                <h3>{chapter.title}</h3>

                <p>{chapter.body}</p>

                <ul>
                  {chapter.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Count({
  value,
  suffix = '',
}: {
  value: number;
  suffix?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    let frame = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        const start = performance.now();

        const tick = (time: number) => {
          const progress = Math.min((time - start) / 900, 1);
          const eased = 1 - Math.pow(1 - progress, 4);

          setDisplay(Math.round(value * eased));

          if (progress < 1) {
            frame = requestAnimationFrame(tick);
          }
        };

        frame = requestAnimationFrame(tick);
        observer.disconnect();
      },
      {
        threshold: 0.4,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value]);

  return (
    <span className="stat__value" ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

function Evidence() {
  const stats = [
    { value: 11, label: 'years in practice' },
    { value: 485, label: 'projects delivered' },
    { value: 100, suffix: '%', label: 'project completion rate' },
  ];

  return (
    <section className="evidence" id="evidence">
      <div className="evidence__intro">
        <p className="eyebrow">A record of delivery</p>

        <h2>Built to survive review, launch, and daily use.</h2>

        <p>
          Good design has to work beyond the presentation. It must
          hold up through approvals, production, changing
          requirements, and daily use. We account for that from the
          beginning.
        </p>
      </div>

      <div className="stats">
        {stats.map((stat) => (
          <article className="stat" key={stat.label}>
            <Count value={stat.value} suffix={stat.suffix} />
            <span>{stat.label}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section className="partners" id="partners">
      <div className="partners__intro">
        <p className="eyebrow">Selected clients</p>

        <h2>Work that earns another brief.</h2>

        <p>
          Eastern Studio has worked across education, media,
          hospitality, healthcare, manufacturing, nonprofits, and
          emerging businesses in Nepal.
        </p>
      </div>

      <div
        className="partner-grid"
        aria-label="Selected client logos"
      >
        {partners.map((partner) => (
          <div
            className="partner"
            key={partner.name}
            title={partner.name}
          >
            <img
              className="partner__image"
              src={partner.src}
              alt={partner.name}
              loading="lazy"
              decoding="async"
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" id="contact">
      <div className="footer__main">
        <div>
          <p className="eyebrow">
            New work / thoughtful collaborations
          </p>

          <h2>
            Let’s build something
            <br />
            worth <em>keeping.</em>
          </h2>
        </div>

        <a
          className="footer__email"
          href="mailto:design@easternstudio.net"
        >
          <span>Get in touch</span>
          <strong>design@easternstudio.net</strong>
          <Arrow />
        </a>
      </div>

      <div className="footer__meta">
        <a
          className="brand footer__brand"
          href="#top"
          aria-label="Eastern Studio home"
        >
          <img
            className="brand__logo"
            src="/brand/eastern_studio_logo.png"
            alt=""
          />
        </a>

        <div className="footer__detail">
          <span>Studio</span>

          <address>
            Kalopul, Kathmandu
          </address>
        </div>

        <div className="footer__detail footer__detail--domain">
          <span>Domain update</span>

          <p>
            eastern.studio is now easternstudio.net since January 2026.
          </p>
        </div>

        <p className="footer__copyright">
          © {new Date().getFullYear()} Eastern Studio
        </p>
      </div>
    </footer>
  );
}

export default function App() {
  useScrollScene();

  return (
    <>
      <div className="scroll-progress" aria-hidden="true" />

      <Header />

      <main>
        <Hero />
        <Story />
        <Evidence />
        <Partners />
      </main>

      <Footer />
    </>
  );
}